import React, { useState, useCallback } from 'react';
import { Ticket } from 'lucide-react';
import WizardShell from '../wizards/WizardShell';
import WizardSuccess from '../wizards/WizardSuccess';
import NameStep from '../wizards/steps/NameStep';
import TournamentStep, { tournamentName } from '../wizards/steps/TournamentStep';
import ReviewRow from '../wizards/steps/ReviewRow';
import InviteStep from '../wizards/steps/InviteStep';
import GafferStep from '../wizards/steps/GafferStep';
import ManagerPowersStep from '../wizards/steps/ManagerPowersStep';
import * as gamesApi from '../../api/games';

/**
 * One call, and it ends on the game's own code.
 *
 * It used to close itself the moment the POST returned, which made the review step's
 * "you'll get a game code to share" a promise nothing kept - the manager had to find
 * the game in the list and open it to get the one thing they needed next. The created
 * game is held instead and handed to the same success panel the league wizard uses.
 */
function CreateGameWizard({ tournaments, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [selection, setSelection] = useState({ tournamentId: '', startingMatchWeek: '' });
  const [gafferEnabled, setGafferEnabled] = useState(false);
  const [inviteeIds, setInviteeIds] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdGame, setCreatedGame] = useState(null);

  const handleSelectionChange = useCallback((next) => setSelection(next), []);

  const chosenTournament = tournaments.find(
    (t) => String(t.id) === String(selection.tournamentId)
  );

  const handleComplete = async () => {
    setError('');
    setSubmitting(true);
    try {
      const game = await gamesApi.createGame({
        name,
        tournamentId: parseInt(selection.tournamentId),
        startingMatchWeek: parseInt(selection.startingMatchWeek),
        gafferEnabled,
        inviteePlayerIds: inviteeIds,
      });
      setCreatedGame(game || {});
    } catch (err) {
      if (err.name === 'ApiError') {
        setError(err.body?.error || 'Failed to create game');
      } else {
        setError('Error creating game');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (createdGame) {
    return (
      <WizardSuccess
        title="Game Created"
        icon={Ticket}
        heading={`${name} is live`}
        blurb="Share this code and your mates are in. No sign-up dance, no invitation needed."
        code={createdGame.gameCode}
        codeLabel="Copy game code"
        footnote={`${tournamentName(chosenTournament)}, kicking off week ${selection.startingMatchWeek}.`}
        onDone={onCreated}
      />
    );
  }

  const steps = [
    {
      id: 'name',
      label: 'Name',
      isComplete: () => name.trim().length > 0,
      render: () => (
        <NameStep
          label="Game Name"
          placeholder="e.g., Office League 2024"
          hint="Your mates will see this name when they join"
          value={name}
          onChange={setName}
        />
      ),
    },
    {
      id: 'tournament',
      label: 'Tournament',
      isComplete: () => Boolean(selection.tournamentId && selection.startingMatchWeek),
      render: () => (
        <TournamentStep
          tournaments={tournaments}
          value={selection}
          onChange={handleSelectionChange}
        />
      ),
    },
    {
      id: 'gaffer',
      label: 'The Gaffer',
      isComplete: () => true,
      render: () => (
        <GafferStep
          checked={gafferEnabled}
          onChange={setGafferEnabled}
          intro="You can change your mind right up until week 1 locks."
        />
      ),
    },
    {
      id: 'invite',
      label: 'Invite',
      isComplete: () => true,
      render: () => (
        <InviteStep
          selectedIds={inviteeIds}
          onToggle={(id) =>
            setInviteeIds((prev) =>
              prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
            )
          }
        />
      ),
    },
    {
      id: 'powers',
      label: 'Manager',
      isComplete: () => true,
      render: () => <ManagerPowersStep kind="standalone" />,
    },
    {
      id: 'review',
      label: 'Review',
      isComplete: () => true,
      render: () => (
        <div className="review-panel">
          <ReviewRow label="Game name" value={name} />
          <ReviewRow label="Tournament" value={tournamentName(chosenTournament)} />
          <ReviewRow label="Starts" value={`Week ${selection.startingMatchWeek}`} />
          <ReviewRow label="The Gaffer" value={gafferEnabled ? 'Playing' : 'Not playing'} />
          <ReviewRow label="Run by" value="You" />
          <p className="helper-text">
            You'll get a game code to share once the game is created.
          </p>
        </div>
      ),
    },
  ];

  return (
    <WizardShell
      title="New One-off Game"
      steps={steps}
      submitLabel="Create Game"
      busyLabel="Creating..."
      error={error}
      submitting={submitting}
      onComplete={handleComplete}
      onClose={onClose}
    />
  );
}

export default CreateGameWizard;
